import React, { useState } from 'react';

interface UserSettingsProps {
  onSave: (settings: any) => Promise<void>;
  isSaving: boolean;
}

const UserSettings: React.FC<UserSettingsProps> = ({ onSave, isSaving }) => {
  const [formData, setFormData] = useState({
    enableUserSignup: false,
    requireApproval: true,
    defaultRole: 'resident',
    twoFactorEnabled: false,
    passwordMinLength: 8,
    passwordRequireSpecialChar: true,
    sessionTimeout: 60, // minutes
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-md">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900">User Settings</h3>
        <p className="mt-1 text-sm text-gray-500">
          Configure user management and security settings.
        </p>
        
        <div className="mt-6 space-y-6">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="enableUserSignup"
                name="enableUserSignup"
                type="checkbox"
                checked={formData.enableUserSignup}
                onChange={handleInputChange}
                className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="enableUserSignup" className="font-medium text-gray-700">
                Enable User Self-Registration
              </label>
              <p className="text-gray-500">
                Allow users to register accounts without an invitation.
              </p>
            </div>
          </div>
          
          {formData.enableUserSignup && (
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="requireApproval"
                  name="requireApproval"
                  type="checkbox"
                  checked={formData.requireApproval}
                  onChange={handleInputChange}
                  className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="requireApproval" className="font-medium text-gray-700">
                  Require Admin Approval
                </label>
                <p className="text-gray-500">
                  New user registrations require administrator approval before account activation.
                </p>
              </div>
            </div>
          )}
          
          <div>
            <label htmlFor="defaultRole" className="block text-sm font-medium text-gray-700">
              Default Role for New Users
            </label>
            <div className="mt-1">
              <select
                id="defaultRole"
                name="defaultRole"
                value={formData.defaultRole}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              >
                <option value="resident">Resident</option>
                <option value="accountant">Accountant</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="twoFactorEnabled"
                name="twoFactorEnabled"
                type="checkbox"
                checked={formData.twoFactorEnabled}
                onChange={handleInputChange}
                className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="twoFactorEnabled" className="font-medium text-gray-700">
                Enable Two-Factor Authentication
              </label>
              <p className="text-gray-500">
                Require two-factor authentication for all users.
              </p>
            </div>
          </div>
          
          <div className="sm:col-span-3">
            <label htmlFor="passwordMinLength" className="block text-sm font-medium text-gray-700">
              Minimum Password Length
            </label>
            <div className="mt-1">
              <input
                type="number"
                name="passwordMinLength"
                id="passwordMinLength"
                min={6}
                max={30}
                value={formData.passwordMinLength}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="passwordRequireSpecialChar"
                name="passwordRequireSpecialChar"
                type="checkbox"
                checked={formData.passwordRequireSpecialChar}
                onChange={handleInputChange}
                className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="passwordRequireSpecialChar" className="font-medium text-gray-700">
                Require Special Characters
              </label>
              <p className="text-gray-500">
                Require passwords to contain at least one special character.
              </p>
            </div>
          </div>
          
          <div className="sm:col-span-3">
            <label htmlFor="sessionTimeout" className="block text-sm font-medium text-gray-700">
              Session Timeout (minutes)
            </label>
            <div className="mt-1">
              <input
                type="number"
                name="sessionTimeout"
                id="sessionTimeout"
                min={5}
                max={1440}
                value={formData.sessionTimeout}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Time of inactivity after which users are automatically logged out.
            </p>
          </div>
        </div>
      </div>
      
      <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

export default UserSettings;
